import type {
  ArrowDefinition,
  BlockerInfo,
  LevelDefinition,
  RemovalResult,
} from './types';
import { cellKey, directionVectors, distanceAhead, isAhead } from './cells';

export class Board {
  readonly level: LevelDefinition;
  private readonly arrowsById: ReadonlyMap<string, ArrowDefinition>;
  private activeArrowIds: Set<string>;

  constructor(level: LevelDefinition) {
    this.validate(level);
    this.level = level;
    this.arrowsById = new Map(level.arrows.map((arrow) => [arrow.id, arrow]));
    this.activeArrowIds = new Set(this.arrowsById.keys());
  }

  get totalCount(): number {
    return this.arrowsById.size;
  }

  get remainingCount(): number {
    return this.activeArrowIds.size;
  }

  get isComplete(): boolean {
    return this.remainingCount === 0;
  }

  get activeArrows(): readonly ArrowDefinition[] {
    return [...this.activeArrowIds]
      .map((id) => this.arrowsById.get(id))
      .filter((arrow): arrow is ArrowDefinition => arrow !== undefined);
  }

  getArrow(id: string): ArrowDefinition | undefined {
    if (!this.activeArrowIds.has(id)) {
      return undefined;
    }
    return this.arrowsById.get(id);
  }

  getBlockerIds(id: string): readonly string[] {
    return this.getBlockers(id).map((blocker) => blocker.id);
  }

  getBlockers(id: string): readonly BlockerInfo[] {
    const arrow = this.getArrow(id);
    if (arrow === undefined) {
      return [];
    }

    const tip = arrow.cells.at(-1);
    if (tip === undefined) {
      return [];
    }

    return this.activeArrows
      .filter((candidate) => candidate.id !== id)
      .map((candidate): BlockerInfo | null => {
        const distances = candidate.cells
          .map((cell) => distanceAhead(tip, cell, arrow.direction))
          .filter((distance): distance is number => distance !== null);
        if (distances.length === 0) {
          return null;
        }
        return { id: candidate.id, distance: Math.min(...distances) };
      })
      .filter((blocker): blocker is BlockerInfo => blocker !== null)
      .sort((left, right) => left.distance - right.distance || left.id.localeCompare(right.id));
  }

  getNearestBlocker(id: string): BlockerInfo | undefined {
    return this.getBlockers(id)[0];
  }

  canRemove(id: string): boolean {
    const arrow = this.getArrow(id);
    const tip = arrow?.cells.at(-1);
    if (arrow === undefined || tip === undefined) {
      return false;
    }

    for (const candidateId of this.activeArrowIds) {
      if (candidateId === id) {
        continue;
      }
      const candidate = this.arrowsById.get(candidateId);
      if (candidate?.cells.some(
        (cell) => distanceAhead(tip, cell, arrow.direction) !== null,
      )) {
        return false;
      }
    }
    return true;
  }

  get removableArrowIds(): readonly string[] {
    return this.activeArrows
      .filter((arrow) => this.canRemove(arrow.id))
      .map((arrow) => arrow.id);
  }

  tryRemove(id: string): RemovalResult {
    if (this.getArrow(id) === undefined) {
      return { kind: 'missing', blockerIds: [] };
    }

    const blockers = this.getBlockers(id);
    const nearestBlocker = blockers[0];
    if (nearestBlocker !== undefined) {
      return {
        kind: 'blocked',
        blockerIds: blockers.map((blocker) => blocker.id),
        nearestBlockerId: nearestBlocker.id,
        distanceToBlocker: nearestBlocker.distance,
      };
    }

    this.activeArrowIds.delete(id);
    return { kind: 'removed', blockerIds: [] };
  }

  reset(): void {
    this.activeArrowIds = new Set(this.arrowsById.keys());
  }

  private validate(level: LevelDefinition): void {
    // 整数グリッドが盤面の契約。difficulty 側の TypedArray 索引はこの保証に依存する
    if (!Number.isInteger(level.width) || !Number.isInteger(level.height)) {
      throw new Error('Level dimensions must be integers.');
    }
    if (level.width < 1 || level.height < 1) {
      throw new Error('Level dimensions must be positive.');
    }

    const ids = new Set<string>();
    const occupiedCells = new Map<string, string>();

    for (const arrow of level.arrows) {
      if (ids.has(arrow.id)) {
        throw new Error(`Duplicate arrow id: ${arrow.id}`);
      }
      ids.add(arrow.id);

      if (arrow.cells.length === 0) {
        throw new Error(`Arrow ${arrow.id} must contain at least one cell.`);
      }

      const tip = arrow.cells.at(-1);
      const beforeTip = arrow.cells.at(-2);
      if (tip !== undefined && beforeTip !== undefined) {
        const expected = directionVectors[arrow.direction];
        if (tip.x - beforeTip.x !== expected.x || tip.y - beforeTip.y !== expected.y) {
          throw new Error(`Arrow ${arrow.id} tip does not match its direction.`);
        }
      }

      arrow.cells.forEach((cell, index) => {
        if (!Number.isInteger(cell.x) || !Number.isInteger(cell.y)) {
          throw new Error(`Arrow ${arrow.id} has a non-integer cell coordinate.`);
        }
        if (cell.x < 0 || cell.x >= level.width || cell.y < 0 || cell.y >= level.height) {
          throw new Error(`Arrow ${arrow.id} is outside the board.`);
        }

        const previous = arrow.cells[index - 1];
        if (previous !== undefined) {
          const distance = Math.abs(previous.x - cell.x) + Math.abs(previous.y - cell.y);
          if (distance !== 1) {
            throw new Error(`Arrow ${arrow.id} contains non-adjacent cells.`);
          }
        }

        const key = cellKey(cell);
        const occupyingArrow = occupiedCells.get(key);
        if (occupyingArrow !== undefined) {
          throw new Error(`Arrows ${occupyingArrow} and ${arrow.id} overlap at ${key}.`);
        }
        occupiedCells.set(key, arrow.id);
      });

      if (tip !== undefined && arrow.cells.some((cell) => isAhead(tip, cell, arrow.direction))) {
        throw new Error(`Arrow ${arrow.id} extends beyond its own tip.`);
      }
    }
  }
}
